import axios, { InternalAxiosRequestConfig } from "axios";
import { API_V1_ADMIN_BASE } from "../config/api";
import { store } from "../redux/store";
import { logout, replaceSessionTokens } from "../redux/authSlice";
import { getTokenExpiration, isTokenCurrent } from "../utils/tokenUtils";
import { retryAfterSeconds } from "../utils/apiError";

const options = { baseURL: API_V1_ADMIN_BASE, headers: { "Content-Type": "application/json" }, timeout: 20000 };
// Password login, MFA, invitation acceptance and refresh never use dashboard interceptors.
export const publicApiClient = axios.create(options);
const apiClient = axios.create(options);
type SessionRequest = InternalAxiosRequestConfig & { _navoRetried?: boolean; _navoGeneration?: number };
let refreshing: { generation: number; promise: Promise<string> } | null = null;
let refreshBlockedUntil = 0;
let refreshFailure: unknown;
let blockedGeneration = -1;

export function refreshAdminSession(): Promise<string> {
  const generation = store.getState().auth.sessionGeneration;
  if (refreshing?.generation === generation) return refreshing.promise;
  if (Date.now() < refreshBlockedUntil && blockedGeneration === store.getState().auth.sessionGeneration) return Promise.reject(refreshFailure);
  const previous = store.getState().auth.refreshToken;
  if (!previous) { store.dispatch(logout()); return Promise.reject(new Error("Your session has expired. Please sign in again.")); }
  const pending: Promise<string> = (async () => {
    try {
      const { data } = await publicApiClient.post<{ accessToken: string; refreshToken: string }>("auth/refresh-token", { refreshToken: previous });
      // Logout or a new sign-in while refresh was in flight must win.
      const current = store.getState().auth;
      if (current.sessionGeneration !== generation) throw new Error("The sign-in session changed.");
      if (current.refreshToken !== previous) {
        if (current.accessToken && isTokenCurrent(current.accessToken)) return current.accessToken;
        throw new Error("The sign-in session changed.");
      }
      const exp = getTokenExpiration(data.accessToken || "");
      if (!exp || exp * 1000 <= Date.now() || !data.refreshToken || data.refreshToken === previous) {
        store.dispatch(logout());
        throw new Error("Unable to renew your session. Please sign in again.");
      }
      store.dispatch(replaceSessionTokens({ ...data, exp }));
      return data.accessToken;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if ([400, 401].includes(error.response?.status || 0) && store.getState().auth.sessionGeneration === generation && store.getState().auth.refreshToken === previous) store.dispatch(logout());
        const wait = retryAfterSeconds(error);
        if (wait) { refreshBlockedUntil = Date.now() + wait * 1000; refreshFailure = error; blockedGeneration = generation; }
      }
      throw error;
    } finally { if (refreshing?.generation === generation) refreshing = null; }
  })();
  refreshing = { generation, promise: pending };
  return pending;
}

apiClient.interceptors.request.use(async config => {
  const request = config as SessionRequest;
  const before = store.getState().auth;
  // Requests queued by an older admin must not run under a different account.
  if (request._navoGeneration !== undefined && request._navoGeneration !== before.sessionGeneration) throw new Error("The sign-in session changed.");
  request._navoGeneration = before.sessionGeneration;
  const token = isTokenCurrent(before.accessToken) ? before.accessToken : await refreshAdminSession();
  if (before.sessionGeneration !== store.getState().auth.sessionGeneration) throw new Error("The sign-in session changed.");
  request.headers.Authorization = `Bearer ${token}`;
  if (request.data instanceof FormData) delete request.headers["Content-Type"];
  return request;
});

apiClient.interceptors.response.use(response => response, async error => {
  const request = error.config as SessionRequest | undefined;
  if (error.response?.status !== 401 || !request) return Promise.reject(error);
  const current = store.getState().auth;
  if (request._navoGeneration !== current.sessionGeneration) return Promise.reject(error);
  if (request._navoRetried) {
    if (request.headers.Authorization === `Bearer ${current.accessToken}`) store.dispatch(logout());
    return Promise.reject(error);
  }
  request._navoRetried = true;
  try {
    // A concurrent request may already have rotated this access token.
    const token = current.accessToken && request.headers.Authorization !== `Bearer ${current.accessToken}` && isTokenCurrent(current.accessToken)
      ? current.accessToken : await refreshAdminSession();
    request.headers.Authorization = `Bearer ${token}`;
    return apiClient(request);
  } catch (refreshError) { return Promise.reject(refreshError); }
});
export default apiClient;
